import {
  againField,
  choicesFrom,
  chosenField,
  questionField,
  typedField,
  versionField,
} from "../content/lianxi/exercise-fields.js";
import {
  isReplyKeys,
  markReply,
  replyMoves,
  replyPasses,
  type YueduReplyKeys,
} from "../fuxi/yuedu-reply-marking.js";
import { fuxiPath } from "../user/fuxi-address.js";
import { afterFinish, offerAfterFinish } from "./lianxi-finish-copy.js";
import type { HandoverCount } from "./lianxi-handover.js";
import { answerHandoverCount } from "./lianxi-handover.js";
import type { ExercisePress, RecordAnswer } from "./lianxi-post.js";
import type { HearVerdict } from "./lianxi-verdict-sounds.js";
import { clearOptions, markOptions } from "./lianxi-xuanze.js";
import { yueduCopy } from "./lianxi-yuedu-copy.js";
import { showStage } from "./yuedu-stage.js";

/**
 * A reading Exercise 阅读练习 on its page: the passage, then each question in
 * turn, each answered once.
 *
 * `partials/lianxi/yuedu.html` draws the passage and every question as a
 * form, hidden, with its options as radio buttons and the right option's
 * place on the form, and `./lianxi-yuedu-start.ts` builds this over them. A
 * question is answered by choosing an option, which marks it at once,
 * ringing the right option and a wrong one chosen, with 复习's note for the
 * verdict. A miss shows the right
 * answer and the reader goes on, since asking a question of three options
 * again after its answer was shown tests nothing (Hugh, 2026-10-22). Every
 * question answered is the finish, which says how many were right.
 *
 * **The first press is the answer** (Hugh, CB-22 and CB-130). A press on an
 * option or its letter is `choose`, which marks the question whether the
 * option is right or wrong, so there is no Check to press. Marking only the
 * right option at once let a reader press each option in turn until one
 * marked itself, and score every question right. The radio buttons' own
 * keys, the arrows and space, only move the choice, and Enter marks the
 * option they moved to, so a keyboard keeps a deliberate answer.
 *
 * **A reply question hides the turns after the one it replies to.** The right
 * reply is the passage's own next turn, so while it is asked the passage
 * stops at the turn, which is marked, and comes back whole once it is
 * answered.
 *
 * **The finish offers the answers again, read-only** (Qing Wang, CB-40 and
 * CB-41). Review answers shows each question as it was marked, the reader's
 * own choice and the right option, with a numbered button per question that
 * says whether it was answered right. Nothing in the review can be chosen,
 * and it leaves the score alone. The choice behind a kept miss comes back
 * from the row, and a row from before the choices were kept shows the right
 * option alone.
 *
 * **A staged passage shows its lines as the questions go** (CB-77). Each
 * question names the lines shown while it is asked (`./yuedu-stage.ts`), and
 * an answer shows the lines the next question names. The finish shows the
 * whole passage, and a reload shows the lines of the question at the head of
 * the kept queue.
 *
 * **A written reply is typed, and given a second try.** It is marked against
 * the checked replies on its form (`src/fuxi/yuedu-reply-marking.ts`). A
 * known wrong reply says what is wrong with it, and one nobody has checked
 * says so and is never called wrong. Either shows the hint and asks once
 * more, and the second reply ends the question with the checked replies
 * shown. A reply nobody checked is left out of the score.
 *
 * **A signed-in reader's answers are kept.** Each is posted through the
 * keeper, and the handler's answer is the queue as the row holds it, which
 * the page adopts. A press that cannot land moves the page on its own
 * arithmetic, as a grammar exercise's does.
 */

/** A reader's place, as the handler or the page holds it. */
export interface YueduPlace {
  readonly queue: readonly number[];
  readonly missed: readonly number[];

  /** The option chosen for each question, or -1 where none is known. */
  readonly choices?: readonly number[] | undefined;

  /** The reply last written to each question, empty where none was. */
  readonly replies?: readonly string[] | undefined;

  /** The tries made at each written reply. */
  readonly tries?: readonly number[] | undefined;

  /** The written replies ended on a reply nobody checked. */
  readonly unchecked?: readonly number[] | undefined;
}

/** What the review of a finished Exercise is drawn with. */
export interface YueduReviewParts {
  /** What holds the numbered buttons and the moves, hidden until a review. */
  readonly nav: HTMLElement;

  /** A button per question, in the order asked. */
  readonly marks: readonly HTMLButtonElement[];
  readonly previous: HTMLButtonElement;
  readonly next: HTMLButtonElement;

  /** The button back to the finish. */
  readonly back: HTMLButtonElement;

  /** The finish's button into the review, which the way back focuses. */
  readonly open?: HTMLButtonElement | null | undefined;
}

/** What keeps a signed-in reader's place: the press and what it has heard. */
export interface YueduKeeper {
  readonly press: Pick<ExercisePress, "post">;

  /** The version the page was drawn from, which every answer names. */
  readonly version: string;
  handedOver: boolean;
  handoverCount?: HandoverCount | undefined;

  /** Whether the reader is on a free account, who is offered Pro at the finish. */
  readonly free: boolean;
}

/** What the Exercise is built over. */
export interface YueduExerciseProperties {
  /** Every question's form, in the order asked. */
  readonly questions: readonly HTMLFormElement[];

  /** Every line of the passage, in order. */
  readonly lines: readonly HTMLElement[];

  /**
   * The passage's body, which holds the stage a staged passage is at and its
   * New Words rows. Nothing where the page draws no stages.
   */
  readonly body?: HTMLElement | undefined;
  readonly progress: HTMLElement;

  /** The bar under the progress line, filled to the questions answered. */
  readonly meter?: HTMLElement | null | undefined;
  readonly finished: HTMLElement;

  /** The score at the finish. */
  readonly score: HTMLElement;

  /** The share right beside the score, where the page draws one. */
  readonly share?: HTMLElement | null | undefined;

  /** The hand-over under the score, and the offer of Pro. */
  readonly finishedLine: HTMLElement;

  /** The review of the answers, where the page draws one. */
  readonly review?: YueduReviewParts | undefined;

  /** Where the handler left the reader, for a signed-in reader. */
  readonly kept?: YueduPlace | undefined;
  readonly keeper?: YueduKeeper | undefined;

  /**
   * Whether the question at a place is past a visitor's taste, which shows
   * the gate in its place. Nothing for a reader who plays to the end.
   */
  readonly gate?: ((at: number) => boolean) | undefined;

  /** What plays the note for a verdict. Nothing for a silent Exercise. */
  readonly hear?: HearVerdict | undefined;
}

/** A question's options, as radio buttons in the order drawn. */
export function optionsOf(form: HTMLFormElement): readonly HTMLInputElement[] {
  return [...form.querySelectorAll<HTMLInputElement>('input[type="radio"]')];
}

/** A part of a form, by the name after `data-cb-yuedu-`. */
function partOf(form: HTMLElement, name: string): HTMLElement | null {
  return form.querySelector<HTMLElement>(`[data-cb-yuedu-${name}]`);
}

/**
 * Say something under a question: the mark, or a request to choose. The hint
 * on the keys gives way to it, and comes back when there is nothing to say.
 */
function say(form: HTMLElement, text: string): void {
  const mark = partOf(form, "mark");
  const hint = partOf(form, "hint");

  if (mark !== null) {
    mark.textContent = text;
  }

  if (hint !== null) {
    hint.hidden = text !== "";
  }
}

/** A written reply's field, or nothing on a question of options. */
function replyField(form: HTMLFormElement): HTMLInputElement | null {
  return form.querySelector<HTMLInputElement>("[data-cb-yuedu-reply]");
}

/** What a written reply's form is marked against, or nothing for any other. */
export function writtenOf(form: HTMLElement): YueduReplyKeys | undefined {
  const said = form.dataset["cbYueduWritten"];

  if (said === undefined) {
    return undefined;
  }

  try {
    const keys: unknown = JSON.parse(said);

    return isReplyKeys(keys) ? keys : undefined;
  } catch {
    return undefined;
  }
}

/** The lines a question of a staged passage shows, or nothing. */
function shownOf(form: HTMLElement): number | undefined {
  const shown = Number(form.dataset["cbYueduShown"] ?? "");

  return Number.isInteger(shown) && shown > 0 ? shown : undefined;
}

/** A list read back one per question, or the fallback for each. */
function perQuestion<T>(
  read: readonly T[] | undefined,
  count: number,
  fallback: T,
): readonly T[] {
  return read?.length === count
    ? read
    : Array.from({ length: count }, () => fallback);
}

/** Whole numbers, or nothing where the value is not a list of them. */
export function wholeNumbers(value: unknown): readonly number[] | undefined {
  return Array.isArray(value) &&
    value.every((each) => Number.isInteger(each) && Number(each) >= 0)
    ? (value as number[])
    : undefined;
}

/** A reading Exercise on its page. */
export class YueduExercise {
  #queue: number[] = [];
  #missed: number[] = [];

  /** The option chosen for each question, or -1 where none is known. */
  #choices: number[] = [];

  /** The reply last written to each question, empty where none was. */
  #replies: string[] = [];

  /** The tries made at each written reply. */
  #tries: number[] = [];

  /** The written replies ended on a reply nobody checked. */
  #unchecked: number[] = [];

  /** The question the review shows, or nothing outside a review. */
  #reviewing: number | undefined;

  /** The posts sent so far, which says whether an answer is the newest. */
  #sent = 0;

  /** The post under way, which the next one waits on. */
  #posting: Promise<void> = Promise.resolve();

  constructor(private readonly properties: YueduExerciseProperties) {}

  /** Start at the kept place, or at the first question. */
  start(): void {
    const { kept, questions } = this.properties;

    this.#queue = [...(kept?.queue ?? questions.map((_, at) => at))];
    this.#missed = [...(kept?.missed ?? [])];
    this.#choices = [
      ...(choicesFrom(kept?.choices, questions.length) ?? this.#unchosen()),
    ];
    this.#replies = [...perQuestion(kept?.replies, questions.length, "")];
    this.#tries = [...perQuestion(kept?.tries, questions.length, 0)];
    this.#unchecked = [...(kept?.unchecked ?? [])];
    this.#showHead();
  }

  /**
   * Choose an option of the question showing, and mark it.
   * @param at - The question's place in the order asked.
   * @param option - The option's place in the order drawn.
   * @returns The radio button chosen, or nothing where none could be.
   */
  choose(at: number, option: number): HTMLInputElement | undefined {
    const form = this.properties.questions[at];
    const options = form === undefined ? [] : optionsOf(form);
    const radio = options[option];

    if (
      form === undefined ||
      radio === undefined ||
      radio.value === "" ||
      this.#queue[0] !== at ||
      options.some((each) => each.disabled)
    ) {
      return undefined;
    }

    radio.checked = true;
    this.check(at);

    return radio;
  }

  /**
   * Mark the question showing, by the option chosen.
   * @param at - The question's place in the order asked.
   */
  check(at: number): void {
    const form = this.properties.questions[at];
    const options = form === undefined ? [] : optionsOf(form);
    const keys = form === undefined ? undefined : writtenOf(form);

    if (form !== undefined && keys !== undefined && this.#queue[0] === at) {
      this.#checkReply(at, form, keys);

      return;
    }

    if (form === undefined || options.length === 0 || this.#queue[0] !== at) {
      return;
    }

    const picked = options.find((option) => option.checked);

    if (picked === undefined || picked.value === "") {
      say(form, yueduCopy.chooseFirst);

      return;
    }

    const chosen = Number(picked.value);
    const answer = Number(form.dataset["cbYueduAnswer"]);
    const right = chosen === answer;

    this.#queue = this.#queue.slice(1);
    this.#missed = right ? this.#missed : [...this.#missed, at];
    this.#choices[at] = chosen;
    this.#showMarked(form, right, answer, picked, options);
    this.#showAfter(form);
    this.properties.hear?.(right);
    void this.#post({
      [questionField]: String(at),
      [chosenField]: String(chosen),
    });
  }

  /** Go on from a marked question, into the next reply's field where it is one. */
  next(): void {
    this.#showHead();

    const form = this.properties.questions[this.#queue[0] ?? -1];

    if (form?.hidden === false) {
      replyField(form)?.focus();
    }
  }

  /** Start the Exercise again from its first question. */
  again(): void {
    const { questions } = this.properties;

    this.#queue = questions.map((_, at) => at);
    this.#missed = [];
    this.#choices = this.#unchosen();
    this.#replies = questions.map(() => "");
    this.#tries = questions.map(() => 0);
    this.#unchecked = [];
    this.#reviewing = undefined;

    for (const form of questions) {
      this.#ready(form);
    }

    void this.#post({ [againField]: "" });
    this.#showHead();
  }

  /**
   * Show a finished Exercise's question as it was marked, read-only.
   * @param at - The question's place in the order asked.
   */
  review(at: number): void {
    const { questions, finished, progress, review } = this.properties;
    const form = questions[at];

    if (form === undefined || review === undefined || this.#queue.length > 0) {
      return;
    }

    const options = optionsOf(form);
    const answer = Number(form.dataset["cbYueduAnswer"]);
    const chosen = this.#choiceOf(at, answer);
    const right = !this.#missed.includes(at);

    this.#reviewing = at;

    for (const each of questions) {
      each.hidden = each !== form;
    }

    finished.hidden = true;
    review.nav.hidden = false;
    progress.hidden = false;
    progress.textContent = yueduCopy.progress(at + 1, questions.length);

    if (writtenOf(form) !== undefined) {
      this.#showReplyDone(form, at);
      say(
        form,
        this.#unchecked.includes(at)
          ? yueduCopy.replyUnchecked
          : right
            ? yueduCopy.right
            : yueduCopy.wrong,
      );
      partOf(form, "check")?.setAttribute("hidden", "");
      partOf(form, "next")?.setAttribute("hidden", "");
      this.#showWhole();
      this.#showReviewNav(at);

      return;
    }

    clearOptions(options);

    const picked = chosen === undefined ? undefined : options[chosen];

    if (picked !== undefined) {
      picked.checked = true;
    }

    markOptions(options, (radio) => Number(radio.value) === answer, picked);
    say(form, right ? yueduCopy.right : yueduCopy.wrong);
    partOf(form, "check")?.setAttribute("hidden", "");
    partOf(form, "next")?.setAttribute("hidden", "");
    this.#showWhole();
    this.#showReviewNav(at);
  }

  /** Move the review on by a question, or back by one. */
  reviewBy(step: 1 | -1): void {
    if (this.#reviewing === undefined) {
      return;
    }

    const to = this.#reviewing + step;

    if (to >= 0 && to < this.properties.questions.length) {
      this.review(to);
    }
  }

  /** Whether a review is showing. */
  get reviewing(): boolean {
    return this.#reviewing !== undefined;
  }

  /** Leave the review for the finish, its score as it was. */
  leaveReview(): void {
    if (this.#reviewing === undefined) {
      return;
    }

    this.#reviewing = undefined;
    this.#showHead();
    this.properties.review?.open?.focus();
  }

  /**
   * The option chosen for a question: the one kept, the right one for a
   * question not missed, or nothing for a miss whose choice was not kept.
   */
  #choiceOf(at: number, answer: number): number | undefined {
    const kept = this.#choices[at] ?? -1;

    if (kept >= 0) {
      return kept;
    }

    return this.#missed.includes(at) ? undefined : answer;
  }

  /** Every question with nothing chosen. */
  #unchosen(): number[] {
    return this.properties.questions.map(() => -1);
  }

  /** The numbered buttons and the moves, for the question the review shows. */
  #showReviewNav(at: number): void {
    const { review, questions } = this.properties;

    if (review === undefined) {
      return;
    }

    review.marks.forEach((button, index) => {
      const right = !this.#missed.includes(index);
      const unchecked = this.#unchecked.includes(index);

      button.dataset["cbYueduReviewMark"] = unchecked
        ? "unchecked"
        : right
          ? "right"
          : "wrong";
      button.setAttribute(
        "aria-label",
        unchecked
          ? yueduCopy.reviewUnchecked(index + 1)
          : right
            ? yueduCopy.reviewRight(index + 1)
            : yueduCopy.reviewWrong(index + 1),
      );

      if (index === at) {
        button.setAttribute("aria-current", "step");
      } else {
        button.removeAttribute("aria-current");
      }
    });

    const focused = review.nav.ownerDocument.activeElement;

    review.previous.disabled = at === 0;
    review.next.disabled = at === questions.length - 1;

    // A move that lands on the first or last question disables the button
    // pressed, which would drop the focus to the page, so it goes to the other.
    if (focused === review.previous && review.previous.disabled) {
      review.next.focus();
    } else if (focused === review.next && review.next.disabled) {
      review.previous.focus();
    }
  }

  /** Show the question at the head of the queue, the gate, or the finish. */
  #showHead(): void {
    const { questions, finished, progress, gate, review } = this.properties;
    const at = this.#queue[0];

    for (const form of questions) {
      form.hidden = true;
    }

    finished.hidden = true;
    this.#reviewing = undefined;

    if (review !== undefined) {
      review.nav.hidden = true;
    }

    if (at === undefined) {
      this.#showFinish();

      return;
    }

    if (gate?.(at) === true) {
      progress.hidden = true;
      this.#showWhole();
      this.#showMeter();

      return;
    }

    const form = questions[at];

    if (form === undefined) {
      return;
    }

    progress.hidden = false;
    progress.textContent = yueduCopy.progress(
      questions.length - this.#queue.length + 1,
      questions.length,
    );
    this.#showMeter();
    this.#ready(form);
    form.hidden = false;
    this.#showUpTo(form);
  }

  /** Fill the bar under the progress line to the questions answered. */
  #showMeter(): void {
    const { meter, questions } = this.properties;
    const done = questions.length - this.#queue.length;

    meter?.style.setProperty(
      "--percentage",
      `${String((100 * done) / Math.max(questions.length, 1))}%`,
    );
    meter
      ?.querySelector('[role="progressbar"]')
      ?.setAttribute("aria-valuenow", String(done));
  }

  /** A question as it is before it is answered. */
  #ready(form: HTMLFormElement): void {
    clearOptions(optionsOf(form));
    say(form, "");

    partOf(form, "check")?.removeAttribute("hidden");
    partOf(form, "next")?.setAttribute("hidden", "");

    if (writtenOf(form) !== undefined) {
      this.#readyReply(form);
    }
  }

  /**
   * A written reply as it is before it is answered, or as a reload finds it
   * part way: its first reply back in the field and the hint showing, ready
   * for the second try.
   */
  #readyReply(form: HTMLFormElement): void {
    const at = this.properties.questions.indexOf(form);
    const field = replyField(form);
    const retrying = (this.#tries[at] ?? 0) > 0;

    if (field !== null) {
      field.disabled = false;
      field.value = retrying ? (this.#replies[at] ?? "") : "";
    }

    partOf(form, "field")?.removeAttribute("hidden");
    partOf(form, "help")?.toggleAttribute("hidden", !retrying);
    partOf(form, "replies")?.setAttribute("hidden", "");
    partOf(form, "yours")?.setAttribute("hidden", "");

    if (retrying) {
      say(form, yueduCopy.tryAgain);
    }
  }

  /**
   * Mark a written reply, and either ask once more or end the question.
   * @param at - The question's place in the order asked.
   * @param form - Its form.
   * @param keys - What the reply is marked against.
   */
  #checkReply(at: number, form: HTMLFormElement, keys: YueduReplyKeys): void {
    const field = replyField(form);
    const typed = field?.value.trim() ?? "";

    if (typed === "") {
      say(form, yueduCopy.writeFirst);
      field?.focus();

      return;
    }

    const mark = markReply(typed, keys);
    const before = this.#tries[at] ?? 0;
    const move = replyMoves(mark.outcome, before);
    const why =
      mark.known === undefined ? undefined : keys.known[mark.known]?.why;
    const said =
      mark.outcome === "right"
        ? yueduCopy.right
        : mark.outcome === "other"
          ? yueduCopy.replyOther
          : why === undefined
            ? yueduCopy.replyUnchecked
            : yueduCopy.replyKnown(why);

    this.#tries[at] = before + 1;
    this.#replies[at] = typed;

    if (mark.outcome === "right" || mark.outcome === "known") {
      this.properties.hear?.(mark.outcome === "right");
    } else if (mark.outcome === "other") {
      this.properties.hear?.(true);
    }

    if (!move.done) {
      say(form, `${said} ${yueduCopy.tryAgain}`);
      partOf(form, "help")?.removeAttribute("hidden");
      field?.select();
    } else {
      this.#queue = this.#queue.slice(1);
      this.#missed = move.missed ? [...this.#missed, at] : this.#missed;
      this.#unchecked = move.unchecked
        ? [...this.#unchecked, at]
        : this.#unchecked;
      this.#showReplyDone(form, at);
      say(
        form,
        replyPasses(mark.outcome) ? said : `${said} ${yueduCopy.compare}`,
      );
      partOf(form, "check")?.setAttribute("hidden", "");
      partOf(form, "next")?.removeAttribute("hidden");
      partOf(form, "next")?.focus();
      this.#showAfter(form);
    }

    void this.#post({
      [questionField]: String(at),
      [typedField]: typed,
    });
  }

  /** A written reply once it is done: the reader's reply and the checked ones. */
  #showReplyDone(form: HTMLFormElement, at: number): void {
    const field = replyField(form);
    const yours = partOf(form, "yours");
    const text = yours?.querySelector<HTMLElement>(
      "[data-cb-yuedu-yours-text]",
    );

    if (field !== null) {
      field.value = this.#replies[at] ?? "";
      field.disabled = true;
    }

    // The reply shows once, as the reader's own above the examples.
    partOf(form, "field")?.setAttribute("hidden", "");

    if (text !== null && text !== undefined) {
      text.textContent = this.#replies[at] ?? "";
    }

    yours?.toggleAttribute("hidden", (this.#replies[at] ?? "") === "");
    partOf(form, "help")?.setAttribute("hidden", "");
    partOf(form, "replies")?.removeAttribute("hidden");
  }

  /** What a question says once it is marked. */
  #showMarked(
    form: HTMLFormElement,
    right: boolean,
    answer: number,
    picked: HTMLInputElement,
    options: readonly HTMLInputElement[],
  ): void {
    markOptions(options, (radio) => Number(radio.value) === answer, picked);
    say(form, right ? yueduCopy.right : yueduCopy.wrong);

    partOf(form, "check")?.setAttribute("hidden", "");
    partOf(form, "next")?.removeAttribute("hidden");
    partOf(form, "next")?.focus();
  }

  /**
   * Show the passage as far as a question allows: to its stage in a staged
   * passage, to the turn a reply question replies to, which is marked, or
   * whole for any other question.
   */
  #showUpTo(form: HTMLFormElement): void {
    const turn = form.dataset["cbYueduTurn"];
    const shown = shownOf(form);
    const { body } = this.properties;

    if (shown !== undefined && body !== undefined) {
      showStage(body, shown, turn === undefined ? undefined : Number(turn));

      return;
    }

    if (turn === undefined) {
      this.#showWhole();

      return;
    }

    for (const line of this.properties.lines) {
      const at = Number(line.dataset["cbYueduLine"]);

      line.hidden = at > Number(turn);

      if (at === Number(turn)) {
        line.dataset["cbYueduTurn"] = "";
      } else {
        delete line.dataset["cbYueduTurn"];
      }
    }
  }

  /** Show every line of the passage, none marked. */
  #showWhole(): void {
    const { body, lines } = this.properties;

    if (body !== undefined) {
      showStage(body, undefined);

      return;
    }

    for (const line of lines) {
      line.hidden = false;
      delete line.dataset["cbYueduTurn"];
    }
  }

  /**
   * Show the passage once a question is answered: whole, or in a staged
   * passage as far as the next question's stage, and whole once none is left.
   */
  #showAfter(form: HTMLFormElement): void {
    const next = this.properties.questions[this.#queue[0] ?? -1];
    const shown = next === undefined ? undefined : shownOf(next);
    const { body } = this.properties;

    if (shownOf(form) === undefined || body === undefined) {
      this.#showWhole();

      return;
    }

    showStage(body, shown);
  }

  /** The finish: how many were right, and the hand-over. */
  #showFinish(): void {
    const { finished, score, share, progress, questions } = this.properties;
    const count = questions.length - this.#unchecked.length;
    const right = count - this.#missed.length;

    this.#showWhole();
    this.#showMeter();
    progress.hidden = true;
    finished.hidden = false;
    score.textContent = yueduCopy.score(right, count);

    if (share !== null && share !== undefined) {
      share.textContent =
        this.#unchecked.length === 0
          ? yueduCopy.share(right, count)
          : `${yueduCopy.share(right, count)}. ${yueduCopy.uncheckedNote(this.#unchecked.length)}`;
    }
    this.#writeHandover();
  }

  /**
   * The hand-over under the score, a link into 复习 once the words are there,
   * and the offer of Pro after it.
   */
  #writeHandover(): void {
    const { finishedLine, keeper } = this.properties;

    finishedLine.textContent = afterFinish(
      yueduCopy.handedOver,
      keeper,
      new Date(),
      yueduCopy.added,
    );

    if (keeper?.handedOver === true) {
      const link = finishedLine.ownerDocument.createElement("a");

      link.className = "lianxi-yuedu-fuxi";
      link.href = fuxiPath;
      link.textContent = yueduCopy.startReviewing;
      finishedLine.append(" ", link);
    }

    offerAfterFinish(finishedLine, keeper);
  }

  /**
   * Post a press, and adopt the place the handler answers with.
   *
   * **The posts go one at a time, and only the newest answer is adopted.** A
   * reader can answer a question, go on and answer the next before the first
   * answer is back. Sent together, the second could reach the handler first
   * and be refused as stale, and the first answer, arriving last, would put
   * the page back on the question the reader had just answered. Sent in turn,
   * the handler sees them in the order they were made, and the newest answer
   * holds every one of them. The page never waits on a post, so a slow
   * connection does not hold the reader up.
   */
  async #post(fields: Readonly<Record<string, string>>): Promise<void> {
    const { keeper } = this.properties;

    if (keeper === undefined) {
      return;
    }

    this.#sent += 1;

    const sent = this.#sent;

    this.#posting = this.#posting.then(async () => {
      const answer = await keeper.press.post({
        ...fields,
        [versionField]: keeper.version,
      });

      if (sent === this.#sent) {
        this.#adopt(answer);
      }
    });

    return this.#posting;
  }

  /** The handler's answer, taken over the page's own arithmetic. */
  #adopt(answer: RecordAnswer | undefined): void {
    const { keeper } = this.properties;
    const queue = wholeNumbers(answer?.queue);
    const missed = wholeNumbers(answer?.missed);
    const choices = choicesFrom(
      answer?.choices,
      this.properties.questions.length,
    );

    if (
      keeper === undefined ||
      answer === undefined ||
      queue === undefined ||
      missed === undefined ||
      answer.version !== keeper.version
    ) {
      return;
    }

    const wasFinished = this.#queue.length === 0;

    this.#queue = [...queue];
    this.#missed = [...missed];
    this.#choices = choices === undefined ? this.#choices : [...choices];
    this.#adoptReplies(answer);
    keeper.handedOver = answer.handedOver === true;
    keeper.handoverCount = answerHandoverCount(answer);

    if (wasFinished && queue.length === 0) {
      this.#showFinishLine();
    }
  }

  /** The replies, tries and unchecked questions an answer holds, where it holds them. */
  #adoptReplies(answer: RecordAnswer): void {
    const count = this.properties.questions.length;
    const { replies } = answer;
    const tries = wholeNumbers(answer.tries);
    const unchecked = wholeNumbers(answer.unchecked);

    if (
      Array.isArray(replies) &&
      replies.length === count &&
      replies.every((each) => typeof each === "string")
    ) {
      this.#replies = [...replies];
    }

    if (tries?.length === count) {
      this.#tries = [...tries];
    }

    if (unchecked !== undefined) {
      this.#unchecked = [...unchecked];
    }
  }

  /** The finish line again, once the hand-over is heard of. */
  #showFinishLine(): void {
    if (this.properties.finished.hidden !== false) {
      return;
    }

    this.#writeHandover();
  }
}
